import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import {
	Box,
	Button,
	Card,
	CircularProgress,
	Step,
	StepLabel,
	Stepper,
	Typography
} from '@material-ui/core';
import { useStoreActions, useStoreState } from 'easy-peasy';
import { FastField } from 'formik';
import _e from 'common/localeUtil';
import TextInputField from '../form/TextInputField';
import ImageSelectorField from '../form/ImageSelectorField';
import GenericDialog from './GenericDialog';
import FieldWrapper from '../form/FieldWrapper';
import SEOSubForm from '../form/SEOSubForm';
import { getTemplateConfigs } from '../../utils';
import ToolTipCustomDomain from '../tooltip/ToolTipCustomDomain';
import ListItemSubForm from '../form/ListItemSubForm';

const useStyles = makeStyles(theme => ({
	dialogContent: {
		paddingBottom: 30,
		fontSize: 15
	},
	launchText: {
		color: '#225bab'
	},
	welcomeTitle: {
		fontSize: 30,
		fontWeight: 'bold'
	},
	oneStep: {
		marginBottom: 34
	},
	begin: {
		marginBottom: 14
	},
	subdomainWrapper: {
		display: 'flex',
		'& input': {
			textTransform: 'lowercase'
		},
		'& .domain': {
			height: 37,
			display: 'flex',
			alignItems: 'center'
		}
	},
	formError: {
		color: '#ff453a',
		margin: '1rem 0'
	},
	divider: {
		width: '100%',
		marginTop: theme.spacing(4),
		marginBottom: theme.spacing(2)
	},
	cardContent: {
		padding: theme.spacing(3),
		overflowY: 'scroll'
	},
	dialogActionPanel: {
		display: 'flex',
		flex: 1,
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center'
	},
	avatarPlaceholder: {
		padding: theme.spacing(1),
		textAlign: 'center',
		'& .text': {
			paddingTop: theme.spacing(1)
		}
	}
}));

const WelcomeDialog = props => {
	const {
		open,
		handleClose,
		values,
		submitForm,
		isSubmitting,
		validateForm,
		setFieldValue
	} = props;
	const classes = useStyles();
	const templateName = useStoreState(state => state.template.template);
	const loadTemplate = useStoreActions(actions => actions.template.loadTemplate);
	const templateConfigs = getTemplateConfigs(templateName);

	React.useEffect(() => {
		setFieldValue('submissionSource', 'welcomeWizard');
		return () => {
			setFieldValue('submissionSource', 'default');
		};
	}, [setFieldValue]);

	const [activeStep, setActiveStep] = useState(0);

	const renderSubdomainSubForm = React.useCallback(() => {
		return (
			<Box>
				<Typography variant="body1">{_e('begin_website_setup_url')}</Typography>
				<Box mt={3} className={classes.subdomainWrapper}>
					<TextInputField name="subdomain" placeholder="subdomain" lightBackground />
					<div className="domain">{process.env.REACT_APP_SITE_URL}</div>
				</Box>
				<Box mt={2}>
					<ToolTipCustomDomain />
				</Box>
			</Box>
		);
	}, []);

	const renderProfileSubForm = React.useCallback(() => {
		return (
			<Box>
				<Typography variant="body1">{_e('update_profile_desc')}</Typography>

				<Box mt={2} display="flex" flexDirection="row">
					<Box>
						<Typography variant="subtitle1">{_e('avatar')}</Typography>
						<ImageSelectorField
							avatarSize={195}
							name="extraProfile.profile_picture"
							dialogTitle="Select Image"
							showImageEditor
							maxWidth={600}
							maxHeight={600}
							cropRatio={1}
						/>
					</Box>
					<Box flex={1} pl={2}>
						<Typography variant="subtitle1">{_e('story')}</Typography>
						<FastField
							name="extraProfile.story"
							placeholder={_e('short_bio')}
							component={FieldWrapper}
							control="input"
							type="text"
							fullWidth
							rows={9}
							rowsMax={12}
							multiline
							className="mt-1"
							showErrorAsPopover
						/>
					</Box>
				</Box>
			</Box>
		);
	}, []);

	const renderSocialInfoSubForm = React.useCallback(() => {
		const items = [
			{
				title: _e('contact'),
				fields: [
					{
						label: 'Email *',
						name: 'receiving_email',
						icon: 'fa-at'
					},
					{
						label: _e('phone_number'),
						name: 'mobile_number',
						icon: 'fa-phone'
					}
				]
			},
			{
				title: _e('social'),
				fields: [
					{
						label: 'Facebook',
						name: 'facebook',
						icon: 'fa-facebook'
					},
					{
						label: 'Twitter',
						name: 'twitter',
						icon: 'fa-twitter'
					},
					{
						label: 'Instagram',
						name: 'instagram',
						icon: 'fa-instagram'
					},
					{
						label: 'Linkedin',
						name: 'linkedin',
						icon: 'fa-linkedin'
					},
					{
						label: 'Whatsapp',
						name: 'whatsapp',
						icon: 'fa-whatsapp'
					},
					{
						label: 'Telegram',
						name: 'telegram',
						icon: 'fa-telegram'
					}
				]
			}
		];
		return (
			<Box>
				<Typography variant="body1">{_e('your_social_information_desc')}</Typography>
				<Box>
					{items.map(({ title, fields }, index) => (
						// eslint-disable-next-line react/no-array-index-key
						<Box mt={2} key={`title-${index}`}>
							<Typography variant="body1">{title}</Typography>
							{fields.map(field => (
								<TextInputField
									key={`social-${field.name}`}
									className="mb-3"
									size="large"
									name={`contactForm.${field.name}`}
									iconLeft={field.icon}
									lightBackground
									placeholder={field.label}
									type="text"
								/>
							))}
						</Box>
					))}
				</Box>
			</Box>
		);
	}, []);

	const renderSEOSubForm = React.useCallback(() => {
		return <SEOSubForm variant="full" />;
	}, []);

	const renderAccoladesSubForm = React.useCallback(() => {
		// return null; // test
		return (
			<ListItemSubForm
				name="extraProfile.accolades"
				example={{
					title: '2012 to 2016',
					description: 'Million Dollar Round Table (MDRT)'
				}}
				title={_e('achievements_desc')}
				values={values.extraProfile.accolades}
			/>
		);
	}, [values]);

	const renderServicesSubForm = React.useCallback(() => {
		return (
			<ListItemSubForm
				name="extraProfile.services"
				example={{
					title: _e('will_writing'),
					description: _e('will_writing_desc')
				}}
				title={_e('services_desc')}
				values={values.extraProfile.services}
			/>
		);
	}, [values]);

	const stepsConfigs = [
		{
			type: 'subdomain',
			title: _e('choose_subdomain'),
			renderer: renderSubdomainSubForm
		},
		{
			type: 'profile',
			title: _e('update_profile'),
			renderer: renderProfileSubForm
		},
		{
			type: 'socialInfo',
			title: _e('your_social_information'),
			renderer: renderSocialInfoSubForm
		},
		{
			type: 'seo',
			title: 'SEO',
			renderer: renderSEOSubForm
		}
	];

	if (templateConfigs.accolades?.disabled !== true) {
		stepsConfigs.push({
			type: 'achivements',
			title: _e('achievements'),
			renderer: renderAccoladesSubForm
		});
	}
	if (templateConfigs.services?.disabled !== true) {
		stepsConfigs.push({
			type: 'services',
			title: _e('services'),
			renderer: renderServicesSubForm
		});
	}

	const handleNext = useCallback(async () => {
		if (activeStep === stepsConfigs.length - 1) {
			await submitForm();
			await loadTemplate();
			handleClose();

			return;
		}
		validateForm().then(errors => {
			if (Object.keys(errors).length === 0) {
				setActiveStep(activeStep + 1);
			} else {
				// handleSubmit();
				console.log('x', errors);
			}
		});
	}, [activeStep]);

	const handlePrev = useCallback(() => {
		setActiveStep(activeStep - 1);
	}, [activeStep]);

	const renderContent = () => {
		return (
			<>
				<Stepper alternativeLabel activeStep={activeStep} style={{ padding: 0 }}>
					{stepsConfigs.map(({ type, title }) => (
						<Step key={type}>
							<StepLabel>{title}</StepLabel>
						</Step>
					))}
				</Stepper>
				<Box mt={4}>
					<Card className={classes.cardContent} elevation={0}>
						{stepsConfigs[activeStep].renderer()}
					</Card>
				</Box>
			</>
		);
	};

	const renderDialogActions = () => {
		const isLastStep = activeStep === stepsConfigs.length - 1;
		return (
			<div className={classes.dialogActionPanel}>
				<Button
					disabled={activeStep <= 0}
					variant="outlined"
					color={'primary'}
					onClick={handlePrev}
					size="large"
				>
					{_e('previous')}
				</Button>
				<Button
					disabled={isSubmitting}
					variant={'contained'}
					color={'primary'}
					onClick={handleNext}
					size="large"
				>
					{isLastStep ? _e('submit') : _e('next')}
					{isSubmitting && <CircularProgress style={{ width: '1rem', height: '1rem' }} />}
				</Button>
			</div>
		);
	};

	return (
		<GenericDialog
			handleClose={handleClose}
			maxWidth="md"
			fullWidth
			open={open}
			subtitle={_e('few_steps_closer_launch')}
			content={renderContent()}
			title={_e('welcome_pwb')}
			renderActions={renderDialogActions}
		/>
	);
};

WelcomeDialog.propTypes = {
	open: PropTypes.bool,
	handleClose: PropTypes.func,
	values: PropTypes.object,
	submitForm: PropTypes.func,
	isSubmitting: PropTypes.bool,
	validateForm: PropTypes.func,
	setFieldValue: PropTypes.func
};

export default WelcomeDialog;
